import React from 'react'
import { v4 as uuid } from 'uuid'
import { AnimatePresence, motion } from 'framer-motion'

import { Avatar } from '@wartek-id/avatar'
import { useToast } from '@wartek-id/toast'

import {
  MessageFeedbackActionSheet,
  MessageLikeDislikeActionSheet,
} from 'app/ConversationPage/components/MessageFeedbackActionSheet'
import MessageOptions from 'app/ConversationPage/components/MessageOptions'
import {
  COPY_TO_CLIPBOARD_TOAST_MESSAGE,
  GREETING_MESSAGE,
  GREETING_MESSAGE_FOR_COPY,
  GURU_AI_AVATAR_IMAGE_PATH,
} from 'app/ConversationPage/constants'
import { useSETrackEvent } from 'app/ConversationPage/hooks/useSETrackEvent'
import {
  useConversationEntryPointStore,
  useConversationLandingOnboardingStore,
  useConversationMessageStore,
  useConversationStore,
} from 'stores/conversation'

const ANIMATION_PROPS = {
  initial: { opacity: 0, y: -50 },
  whileInView: {
    opacity: 1,
    y: 0,
  },
  transition: {
    ease: 'easeOut',
    duration: 0.8,
  },
  viewport: { once: true },
}

const AnimateWrapper = ({ children }) => {
  const { entryPointVariant } = useConversationEntryPointStore()
  const { landingOnboardingVariant } = useConversationLandingOnboardingStore()
  const { hasConversationId } = useConversationStore()
  const { hasMessages } = useConversationMessageStore()
  const enableAnimation = !(hasConversationId() && hasMessages())

  const { SETrackConversationPage } = useSETrackEvent()

  React.useEffect(() => {
    if (enableAnimation && Boolean(landingOnboardingVariant)) {
      SETrackConversationPage('GURU_AI_INTRODUCTION_ANIMATION_LOAD', {
        event_trigger: 'GURU_AI_INTRODUCTION_PAGE',
        screen_name: 'GURU_AI_INTRODUCTION_PAGE',
        source: `HOMEPAGE_${entryPointVariant}`,
        variant: landingOnboardingVariant,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingOnboardingVariant, enableAnimation])

  if (enableAnimation) {
    return <motion.div {...ANIMATION_PROPS}>{children}</motion.div>
  }

  return children
}

const ConversationGreeting = () => {
  const conversationId = uuid()
  const messageId = uuid()

  const [isActionSheetOpen, setActionSheetOpen] = React.useState<boolean>(false)
  const [likeDislikeAction, setLikeDislikeAction] = React.useState<string>('')

  const toast = useToast()
  const { SETrackConversationPage } = useSETrackEvent()
  const { isLandingOnboardingFinished, setLandingOnboardingFinished } =
    useConversationLandingOnboardingStore()

  const copyToClipboardHandler = () => {
    toast({
      message: COPY_TO_CLIPBOARD_TOAST_MESSAGE,
      type: 'success',
    })
  }

  const trackLikeDislikeAction = (action: string) => {
    SETrackConversationPage('GURU_AI_LIKE_DISLIKE', {
      ai_chat_session_id: conversationId,
      ai_message_id: messageId,
      page_element: action,
    })
  }

  const likeActionHandler = () => {
    setLikeDislikeAction('like')
    setActionSheetOpen(false)
    trackLikeDislikeAction('LIKE')
  }

  const dislikeActionHandler = () => {
    setLikeDislikeAction('dislike')
    setActionSheetOpen(false)
    trackLikeDislikeAction('DISLIKE')
  }

  React.useLayoutEffect(() => {
    if (!isLandingOnboardingFinished) {
      setLandingOnboardingFinished()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AnimatePresence>
      <AnimateWrapper>
        <section className="bot">
          <div className="p-4 bg-white">
            <div className="flex items-start space-x-4">
              <div className="flex-none">
                <Avatar
                  src={GURU_AI_AVATAR_IMAGE_PATH}
                  name="Asisten Guru"
                  size="md"
                  alt="Bot avatar"
                />
              </div>
              <div>{GREETING_MESSAGE}</div>
            </div>

            <MessageOptions
              onClick={() => {
                setActionSheetOpen(true)
              }}
            />
          </div>
        </section>

        <MessageFeedbackActionSheet
          isOpen={isActionSheetOpen}
          mode="server"
          message={GREETING_MESSAGE_FOR_COPY}
          onClose={() => {
            setActionSheetOpen(false)
          }}
          onCopy={copyToClipboardHandler}
          onLike={likeActionHandler}
          onDislike={dislikeActionHandler}
        />

        <MessageLikeDislikeActionSheet
          messageId={messageId}
          isOpen={Boolean(likeDislikeAction)}
          action={likeDislikeAction}
          onClose={() => {
            setLikeDislikeAction('')
          }}
        />
      </AnimateWrapper>
    </AnimatePresence>
  )
}

export default ConversationGreeting
